import { useEffect, useRef, useState } from 'react';
import {
  Control,
  UseFormGetValues,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { getKencoFlashMileage } from 'lib/api/getKencoFlashMileage';
import { NormalizedLoad } from 'types/Load';

import { ensureMGBRouteStops } from './mgbStops';
import {
  flashMileageLaneKeyFromRouteStops,
  mgbRouteStopsReadyForFlashMileage,
  mgbStopToFlashMileageStop,
} from './utils';

type UseMGBFlashMileageArgs = {
  control: Control<NormalizedLoad>;
  setValue: UseFormSetValue<NormalizedLoad>;
  getValues: UseFormGetValues<NormalizedLoad>;
  /** When false, skip fetch (e.g. view-only). */
  enabled?: boolean;
};

const DEBOUNCE_MS = 500;

/**
 * When the route has enough address data, fetch Flash miles into rateData.miles
 * (shared by Rate Per Mile and FSC Per Mile). Uses the full ordered stops[] list
 * so multi-stop routes send the correct intermediate count to calculate-distance.
 *
 * Miles are cleared only when the route is incomplete or the lane key changes —
 * not on transient Flash errors, so hydrated/edit values stay usable.
 */
export function useMGBFlashMileage({
  control,
  setValue,
  getValues,
  enabled = true,
}: UseMGBFlashMileageArgs): {
  isLoadingMiles: boolean;
  milesError: string | null;
} {
  const [isLoadingMiles, setIsLoadingMiles] = useState(false);
  const [milesError, setMilesError] = useState<string | null>(null);
  const requestGen = useRef(0);
  const milesLaneKeyRef = useRef<string | null>(null);

  const pickup = useWatch({ control, name: 'pickup' });
  const consignee = useWatch({ control, name: 'consignee' });
  const stops = useWatch({ control, name: 'stops' });
  const transportType = useWatch({
    control,
    name: 'specifications.transportType',
  });
  const transportSize = useWatch({
    control,
    name: 'specifications.transportSize',
  });
  const customerId = useWatch({
    control,
    name: 'customer.externalTMSID',
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const routeValues = { stops, pickup, consignee } as NormalizedLoad;
    const routeStops = ensureMGBRouteStops(routeValues);
    const routeReady = mgbRouteStopsReadyForFlashMileage(routeValues);

    if (!routeReady) {
      setIsLoadingMiles(false);
      setMilesError(null);
      milesLaneKeyRef.current = null;
      setValue('rateData.miles', null, {
        shouldDirty: false,
        shouldValidate: false,
      });
      return;
    }

    const flashStops = routeStops.map((stop) =>
      mgbStopToFlashMileageStop(stop)
    );
    const origin = flashStops[0];
    const destination = flashStops[flashStops.length - 1];
    const equipmentType =
      toKencoFlashEquipmentType(transportSize) ||
      toKencoFlashEquipmentType(transportType) ||
      'Van';
    const laneKey = flashMileageLaneKeyFromRouteStops(
      routeValues,
      equipmentType
    );

    const existingMiles = getValues('rateData.miles');
    const hasExistingMiles =
      existingMiles != null &&
      Number.isFinite(Number(existingMiles)) &&
      Number(existingMiles) > 0;
    if (milesLaneKeyRef.current == null && hasExistingMiles) {
      milesLaneKeyRef.current = laneKey;
    }

    if (
      milesLaneKeyRef.current != null &&
      milesLaneKeyRef.current !== laneKey
    ) {
      setValue('rateData.miles', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      milesLaneKeyRef.current = null;
    }

    if (milesLaneKeyRef.current === laneKey) {
      setIsLoadingMiles(false);
      return;
    }

    const firstStop = routeStops[0];
    const lastStop = routeStops[routeStops.length - 1];
    const pickupDate = toDateOnly(firstStop.readyTime);
    const deliveryDate = toDateOnly(
      lastStop.mustDeliver ?? lastStop.readyEndTime
    );

    let cancelled = false;
    const gen = ++requestGen.current;
    const timer = window.setTimeout(() => {
      void (async () => {
        setIsLoadingMiles(true);
        setMilesError(null);
        const res = await getKencoFlashMileage({
          stops: flashStops,
          origin,
          destination,
          equipmentType,
          customerId: customerId?.trim() || undefined,
          pickupDate,
          deliveryDate,
        });
        if (cancelled || gen !== requestGen.current) {
          if (gen === requestGen.current) {
            setIsLoadingMiles(false);
          }
          return;
        }
        setIsLoadingMiles(false);
        if (res.isErr()) {
          setMilesError(res.error.message);
          return;
        }
        const nextMiles = Number(res.value.miles);
        if (!Number.isFinite(nextMiles) || nextMiles <= 0) {
          setMilesError('Mileage unavailable for this lane');
          return;
        }
        setValue('rateData.miles', nextMiles, {
          shouldDirty: true,
          shouldValidate: true,
        });
        milesLaneKeyRef.current = laneKey;
      })();
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      setIsLoadingMiles(false);
    };
  }, [
    enabled,
    stops,
    pickup,
    consignee,
    transportType,
    transportSize,
    customerId,
    getValues,
    setValue,
  ]);

  return { isLoadingMiles, milesError };
}

function toDateOnly(value: unknown): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const ymd = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (ymd) {
      return ymd[1];
    }
  }
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) {
    return undefined;
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Map MGB equipment id/name to Flash equipmentType (Van | Reefer | Flatbed).
 * Flash rejects MG codes like FT.
 */
function toKencoFlashEquipmentType(
  value: string | null | undefined
): string | undefined {
  if (value == null) {
    return undefined;
  }
  const raw = value.trim();
  if (!raw) {
    return undefined;
  }
  const n = raw.toUpperCase().replace(/[\s_-]+/g, '');
  const map: Record<string, string> = {
    VAN: 'Van',
    VA: 'Van',
    VN: 'Van',
    DV: 'Van',
    DR: 'Van',
    DRYVAN: 'Van',
    REEFER: 'Reefer',
    RF: 'Reefer',
    RFR: 'Reefer',
    RE: 'Reefer',
    FLATBED: 'Flatbed',
    FT: 'Flatbed',
    FB: 'Flatbed',
    FBE: 'Flatbed',
    FD: 'Flatbed',
    FLAT: 'Flatbed',
  };
  if (map[n]) {
    return map[n];
  }
  if (n.includes('FLAT') || n.includes('STEP')) {
    return 'Flatbed';
  }
  if (n.includes('REEF') || n.includes('REFG')) {
    return 'Reefer';
  }
  if (n.includes('VAN')) {
    return 'Van';
  }
  return undefined;
}
